import { useCallback } from "react";
import type React from "react";
import {
  IFamilyConfig,
  ILightingClassConfig,
  ILuminaireLayoutConfig,
  IOptimizationConfig,
  IProjectConfig,
  IRoadLayoutConfig,
  IRoadwayConfig,
  ProjectData,
} from "../../types";
import { getTypedVal, setURL, setFromURLObject, setObjectURL, resetURL } from "../../utils/urls";
import { readFromLocalJsonFile, saveAsJson } from "../../utils/save_as_json";
import { generateProjectFilename } from "../../utils/app.utils";

type ProjectPersistenceParams = {
  // Current state
  portfolioId: string;
  currentStep: number;
  familyConfig: IFamilyConfig;
  roadLayoutConfig: IRoadLayoutConfig;
  roadwayConfig: IRoadwayConfig;
  lightingClassConfig: ILightingClassConfig;
  luminaireLayoutConfig: ILuminaireLayoutConfig;
  optimizationConfig: IOptimizationConfig;
  projectConfig: IProjectConfig;
  selectedResult: string;
  // Setters
  setPortfolioId: React.Dispatch<React.SetStateAction<string>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setFamilyConfig: React.Dispatch<React.SetStateAction<IFamilyConfig>>;
  setRoadLayoutConfig: React.Dispatch<React.SetStateAction<IRoadLayoutConfig>>;
  setRoadwayConfig: React.Dispatch<React.SetStateAction<IRoadwayConfig>>;
  setLightingClassConfig: React.Dispatch<React.SetStateAction<ILightingClassConfig>>;
  setLuminaireLayoutConfig: React.Dispatch<React.SetStateAction<ILuminaireLayoutConfig>>;
  setOptimizationConfig: React.Dispatch<React.SetStateAction<IOptimizationConfig>>;
  setProjectConfig: React.Dispatch<React.SetStateAction<IProjectConfig>>;
  setSelectedResult: React.Dispatch<React.SetStateAction<string>>;
  // Refetch callback
  refetch: () => void;
};

/**
 * Hook to manage project save/load operations.
 */
export function useProjectPersistence(params: ProjectPersistenceParams) {
  const {
    portfolioId,
    currentStep,
    familyConfig,
    roadLayoutConfig,
    roadwayConfig,
    lightingClassConfig,
    luminaireLayoutConfig,
    optimizationConfig,
    projectConfig,
    selectedResult,
    setPortfolioId,
    setCurrentStep,
    setFamilyConfig,
    setRoadLayoutConfig,
    setRoadwayConfig,
    setLightingClassConfig,
    setLuminaireLayoutConfig,
    setOptimizationConfig,
    setProjectConfig,
    setSelectedResult,
    refetch,
  } = params;

  const handleUploadProjectFile = useCallback(
    (projectData: ProjectData) => {
      // Clear all stale URL params before loading new project data
      resetURL();
      // Re-enable stateful URL since resetURL clears it
      setURL("htr_url_statefulurl", true);

      const loadedSelectedResult = projectData.selectedResult ?? "";

      // React state
      setCurrentStep(projectData.currentStep);
      setFamilyConfig(projectData.familyConfig);
      setRoadLayoutConfig(projectData.roadLayoutConfig);
      setRoadwayConfig(projectData.roadwayConfig);
      setLightingClassConfig(projectData.lightingClassConfig);
      setLuminaireLayoutConfig(projectData.luminaireLayoutConfig);
      setOptimizationConfig(projectData.optimizationConfig);
      setProjectConfig(projectData.projectConfig);
      setSelectedResult(loadedSelectedResult);
      setPortfolioId(projectData.portfolio_id ?? "");

      // Sync the full loaded config to URL
      setURL("portfolio_id", projectData.portfolio_id ?? "");
      setURL("selected_result", loadedSelectedResult);
      if (projectData.familyConfig.familyIds.length > 0) {
        setURL("family_ids", projectData.familyConfig.familyIds.join(","));
      }
      setObjectURL(projectData.roadLayoutConfig);
      setObjectURL(projectData.roadwayConfig);
      setObjectURL(projectData.lightingClassConfig);
      setObjectURL(projectData.luminaireLayoutConfig);
      setObjectURL(projectData.optimizationConfig);
      setObjectURL(projectData.projectConfig);

      refetch();
    },
    [
      refetch,
      setCurrentStep,
      setFamilyConfig,
      setRoadLayoutConfig,
      setRoadwayConfig,
      setLightingClassConfig,
      setLuminaireLayoutConfig,
      setOptimizationConfig,
      setProjectConfig,
      setSelectedResult,
      setPortfolioId,
    ]
  );

  const saveProject = useCallback((): void => {
    const projectConfigSnapshot = { ...projectConfig };
    if (getTypedVal("htr_url_statefulurl")) {
      setFromURLObject(projectConfigSnapshot);
    }

    const projectData: ProjectData = {
      portfolio_id: portfolioId,
      currentStep,
      familyConfig,
      roadLayoutConfig,
      roadwayConfig,
      lightingClassConfig,
      luminaireLayoutConfig,
      optimizationConfig,
      projectConfig: projectConfigSnapshot,
      selectedResult,
    };

    const projectName = generateProjectFilename(projectData.projectConfig);
    saveAsJson(projectData, projectName);
  }, [
    projectConfig,
    portfolioId,
    currentStep,
    familyConfig,
    roadLayoutConfig,
    roadwayConfig,
    lightingClassConfig,
    luminaireLayoutConfig,
    optimizationConfig,
    selectedResult,
  ]);

  const openProject = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      readFromLocalJsonFile(event, handleUploadProjectFile);
    },
    [handleUploadProjectFile]
  );

  return {
    saveProject,
    openProject,
    handleUploadProjectFile,
  };
}
